class UnlockCodeService {
  constructor() {
    this.CODE_LENGTH = 8;
  }

  // Generate a random 8-character unlock code (4 letters + 4 numbers)
  generateUnlockCode() {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    
    let code = '';
    
    // First 4 characters: random letters
    for (let i = 0; i < 4; i++) {
      code += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    
    // Last 4 characters: random numbers
    for (let i = 0; i < 4; i++) {
      code += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }
    
    return code;
  }

  // Store unlock code for a business in Supabase
  async storeUnlockCode(businessId, unlockCode) {
    try {
      const { supabase } = await import('../config/supabase');
      
      const { data, error } = await supabase
        .from('businesses')
        .update({ unlock_code: unlockCode })
        .eq('id', businessId)
        .select('unlock_code');

      if (error) {
        console.error('Error storing unlock code:', error);
        return { success: false, error: error.message };
      }

      console.log('Unlock code stored successfully for business:', businessId);
      return { success: true, data };
    } catch (error) {
      console.error('Error in storeUnlockCode:', error);
      return { success: false, error: error.message };
    }
  }

  // Get unlock code for a business from Supabase
  async getUnlockCode(businessId) {
    try {
      const { supabase } = await import('../config/supabase');
      
      const { data, error } = await supabase
        .from('businesses')
        .select('unlock_code')
        .eq('id', businessId)
        .single();

      if (error) {
        console.error('Error getting unlock code:', error);
        return { success: false, error: error.message };
      }

      return { success: true, unlockCode: data.unlock_code };
    } catch (error) {
      console.error('Error in getUnlockCode:', error);
      return { success: false, error: error.message };
    }
  }

  // Validate unlock code for a business
  async validateUnlockCode(businessId, enteredCode) {
    try {
      const { supabase } = await import('../config/supabase');
      
      const { data, error } = await supabase
        .from('businesses')
        .select('unlock_code')
        .eq('id', businessId)
        .eq('unlock_code', enteredCode.toUpperCase())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - invalid code
          return { success: false, valid: false, message: 'Invalid unlock code' };
        }
        console.error('Error validating unlock code:', error);
        return { success: false, error: error.message };
      }

      if (data && data.unlock_code) {
        return { success: true, valid: true, message: 'Unlock code valid' };
      }

      return { success: false, valid: false, message: 'Invalid unlock code' };
    } catch (error) {
      console.error('Error in validateUnlockCode:', error);
      return { success: false, error: error.message };
    }
  }

  // Generate and store a new unlock code for a business
  async generateAndStoreUnlockCode(businessId) {
    try {
      const unlockCode = this.generateUnlockCode();
      const result = await this.storeUnlockCode(businessId, unlockCode);
      
      if (result.success) {
        return { success: true, unlockCode };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error in generateAndStoreUnlockCode:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if unlock code exists for a business
  async hasUnlockCode(businessId) {
    try {
      const result = await this.getUnlockCode(businessId);
      return result.success && result.unlockCode;
    } catch (error) {
      console.error('Error checking unlock code existence:', error);
      return false;
    }
  }

  // Format unlock code for display (add spaces for readability)
  formatUnlockCode(code) {
    if (!code || code.length !== 8) return code;
    return code.slice(0, 4) + ' ' + code.slice(4);
  }
}

// Create singleton instance
const unlockCodeService = new UnlockCodeService();

export default unlockCodeService;
